<template>
    <div>
        <Portal :to="`widget-header__${data.WidgetID}`">
            <div>
                <VcSelect
                    v-model="sortByModel"
                    :multiple="false"
                    :placeholder="t('general.sortBy')"
                    :options="voicebotSortOptions"
                    :config="{ labelKey: 'label', valueKey: 'value' }"
                    class="w-fit min-w-[220px]"
                />
            </div>
        </Portal>
        <div
            ref="parent"
            class="flex justify-center items-start flex-wrap gap-4 py-4"
        >
            <VoicebotCard
                v-for="bot in sortedDebounced"
                :key="bot.VoicebotID"
                :bot="bot"
                :widget-id="data.WidgetID"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import VoicebotCard from '@/components/RedesignComponents/Cards/Voicebots/VoicebotCard.vue'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { VcSelect } from '@voicenter-team/voicenter-ui-plus'
import { useVoiceBotsState } from '@/composables/useVoiceBots'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import { VOICEBOT_LIST_PARAMETR_NAME } from '@/enum/widgetTemplateConfigs'
import { WidgetExtended } from '@/types/dashboard'
import { getVoicebotSortOptions } from '@/enum/voicebotSortOptions'
import orderBy from 'lodash/orderBy'
import { useThrottle } from '@vueuse/core'
import cloneDeep from 'lodash/cloneDeep'
import { useAutoAnimate } from '@formkit/auto-animate/vue'
import { Voicebot } from '@voicenter-team/events-sdk'
import { Portal } from 'portal-vue'

type VoicebotwithMaxMinTimestamp = Voicebot & { _callTimestamp: number }

interface VoicebotsWithWithoutCall {
    withCalls: VoicebotwithMaxMinTimestamp[],
    withoutCalls: Voicebot[]
}

const { t } = useI18n()

const props = defineProps<{
    data: WidgetExtended
}>()

const emit = defineEmits<{
    (event: 'on-update', widgetData: WidgetExtended): void
}>()

const [ parent ] = useAutoAnimate({
    duration: 300
})

const widgetDataRef = ref(cloneDeep(props.data))
const sortByModel = ref('VoicebotName-asc')
const voicebotSortOptions = computed(() => getVoicebotSortOptions(t))
const { allBotsWithCalls } = useVoiceBotsState()

const allBotsWithLiveCalls = computed(() => {
    return allBotsWithCalls.value.map(bot => {
        return {
            ...bot,
            Calls: bot.Calls.filter(call => call.callStatus !== 'Ended')
        }
    })
})

const { filteredItems } = useEntityPositiveNegativeFilters(widgetDataRef, VOICEBOT_LIST_PARAMETR_NAME, allBotsWithLiveCalls, 'VoicebotID')


const sortedBots = computed(() => {
    const [ prop, order ] = sortByModel.value.split('-')

    if (prop === 'callDuration') {
        const { withCalls, withoutCalls } = filteredItems.value.reduce<VoicebotsWithWithoutCall>(
            (acc, bot) => {
                const calls = bot.Calls || []
                if (calls.length) {
                    const timestamp = order === 'asc'
                        ? Math.max(...calls.map(c => c.callStarted))
                        : Math.min(...calls.map(c => c.callStarted))
                    acc.withCalls.push({
                        ...bot,
                        _callTimestamp: timestamp
                    })
                } else {
                    acc.withoutCalls.push(bot)
                }
                return acc
            },
            {
                withCalls: [],
                withoutCalls: []
            }
        )


        const sortedWithCalls = withCalls

            .sort((a, b) =>
                order === 'asc'
                    ? b._callTimestamp - a._callTimestamp
                    : a._callTimestamp - b._callTimestamp
            )


        return order === 'asc'
            ? [ ...sortedWithCalls, ...withoutCalls ]
            : [ ...withoutCalls, ...sortedWithCalls ]
    }

    return orderBy(filteredItems.value, prop, order)
})


const sortedDebounced = useThrottle(sortedBots, 500)

const updateWidget = (widgetData: WidgetExtended) => {
    emit('on-update', widgetData)
}

watch(() => props.data.WidgetLayout?.sortingParams, (sortingParams) => {
    const option = voicebotSortOptions.value.find(o => o.prop === sortingParams?.prop && o.order === sortingParams?.order)
    sortByModel.value = option?.value ?? 'VoicebotName-asc'
}, { immediate: true })

watch(sortByModel, value => {
    const selectedOption = voicebotSortOptions.value.find(o => o.value === value)

    if (selectedOption) {
        widgetDataRef.value.WidgetLayout.sortingParams = {
            prop: selectedOption.prop,
            order: selectedOption.order
        }
        updateWidget(widgetDataRef.value)
    }
})

</script>
